import { useSnackbar } from 'notistack';
import { AttachmentMetaDataModel } from '../../../../api-service/clients';
import { getBlobFromBase64String, getObjectUrlFromBase64String } from '../../../../utilities/blob-helper';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { useFeatureToggles } from '../../../../feature-toggles';
import { PDF_OPEN_AND_DOWNLOAD_TOGGLE, usePdfActions } from '../../../../components/pdf-actions/use-pdf-actions';

const client = moduleClientFactory.createForumCaseworkerClient();

export interface INewsItemAttachmentActions {
  isFetching: boolean;
  onOpen: (newsItemId: string, attachment: AttachmentMetaDataModel) => void;
  onDownload: (newsItemId: string, attachment: AttachmentMetaDataModel) => void;
}

export async function downloadNewsItemAttachment(newsItemId: string, attachment: AttachmentMetaDataModel) {
  const result = await client.getNewsItemAttachment({ newsItemId, documentId: attachment.id });
  if (!result.attachment?.content) {
    return;
  }
  const url = getObjectUrlFromBase64String(result.attachment.content, result.attachment.contentType ?? '');
  const link = document.createElement('a');
  link.href = url;
  link.download = attachment.filename ?? 'bilag';
  link.click();
  URL.revokeObjectURL(url);
}

export function useNewsItemAttachmentDownload() {
  const { enqueueSnackbar } = useSnackbar();

  return async (newsItemId: string, attachment: AttachmentMetaDataModel) => {
    try {
      await downloadNewsItemAttachment(newsItemId, attachment);
    } catch {
      enqueueSnackbar('Kan ikke hente vedhæftningen.', { variant: 'error' });
    }
  };
}

async function fetchNewsItemAttachment(newsItemId: string, attachment: AttachmentMetaDataModel) {
  const result = await client.getNewsItemAttachment({ newsItemId, documentId: attachment.id });
  if (!result.attachment?.content) {
    throw new Error('Vedhæftningen har intet indhold');
  }
  return getBlobFromBase64String(result.attachment.content, result.attachment.contentType ?? '');
}

export function useNewsItemAttachmentActions(): INewsItemAttachmentActions | undefined {
  const { isFeatureEnabled } = useFeatureToggles();
  const { isFetching, openPdf, downloadFile } = usePdfActions();

  if (!isFeatureEnabled(PDF_OPEN_AND_DOWNLOAD_TOGGLE)) {
    return undefined;
  }

  return {
    isFetching,
    onOpen: (newsItemId, attachment) => openPdf(() => fetchNewsItemAttachment(newsItemId, attachment)),
    onDownload: (newsItemId, attachment) => downloadFile(() => fetchNewsItemAttachment(newsItemId, attachment), attachment.filename ?? 'bilag'),
  };
}
